import { Pill } from '../../components/pill';
import { Card } from '../../components/card';
import { PageHead } from '../../components/page-head';
/**
 * Guardian Dashboard
 * Family-account management area for guardians (responsible adults):
 *  - Students: create/edit/deactivate student profiles (name, photo, birth date)
 *  - Devices: link this browser as a family device; list/revoke linked devices
 *  - Account: edit the guardian's own personal data
 * Only reachable with a GUARDIAN JWT (email + password login) — children on a
 * linked family device never see this screen.
 */

import { FormField, Input, FormBlock, FormMessage } from '../../components/form-field';
import { Modal } from '../../components/modal';
import { useState } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../services/authentication/auth-store';
import { authService } from '../../services/authentication/auth-service';
import {
  setFamilyDeviceToken,
  hasFamilyDeviceToken,
} from '../../services/family-device-storage';
import {
  useMyFamily,
  useCreateFamilyStudent,
  useUpdateFamilyStudent,
  useRemoveFamilyStudent,
  useUploadStudentPhoto,
  useFamilyDevices,
  useLinkFamilyDevice,
  useRevokeFamilyDevice,
  useUpdateGuardianProfile,
  useUploadGuardianPhoto,
  useInviteGuardian,
  useRemoveGuardian,
} from '../../api/guardian/family/hooks';
import type { FamilyGuardian, FamilyStudent } from '../../api/guardian/family/types';
import { ProfilePhotoField } from '../shell/profile-photo-field';
import { AppHeader } from '../shell/app-header';
import defaultAvatar from '../../assets/icons/avatars/default-avatar.svg';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import styles from './guardian-dashboard.module.css';

type GuardianSection = 'students' | 'guardians' | 'devices' | 'account';

export function GuardianDashboard() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const [section, setSection] = useState<GuardianSection>('students');

  const SECTIONS: { key: GuardianSection; labelKey: string }[] = [
    { key: 'students', labelKey: 'guardian.nav.students' },
    { key: 'guardians', labelKey: 'guardian.nav.guardians' },
    { key: 'devices', labelKey: 'guardian.nav.devices' },
    { key: 'account', labelKey: 'guardian.nav.account' },
  ];

  return (
    // student-portal: the guardian area follows the student portal's visual
    // identity (elite-purple design system + .nav header pattern)
    <div className={`portal-shell ${styles.guardianPortal} student-portal`}>
      {/* No logout on family devices: leaving the account happens on the profile
          picker (its confirmed logout unlinks the device). Plain logout only
          remains for guardians on unlinked browsers. */}
      <AppHeader
        nav={SECTIONS.map(({ key, labelKey }) => ({
          key,
          label: t(labelKey),
          active: section === key,
          onClick: () => setSection(key),
        }))}
        user={{ name: user?.firstName || user?.name || '', avatarUrl: user?.avatarPhotoUrl || defaultAvatar }}
        onSignOut={() =>
          hasFamilyDeviceToken() ? void authService.switchProfile() : void authService.logout()
        }
        signOutLabel={
          hasFamilyDeviceToken() ? t('dashboard.switchProfile') : t('dashboard.logout')
        }
      />

      <main className={styles.guardianMain}>
        {section === 'students' && <StudentsSection />}
        {section === 'guardians' && <GuardiansSection />}
        {section === 'devices' && <DevicesSection />}
        {section === 'account' && <AccountSection />}
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Students                                                            */
/* ------------------------------------------------------------------ */

interface StudentFormState {
  firstName: string;
  lastName: string;
  birthDate: string;
  /** Optional — lets the student sign in via emailed magic-login link */
  email: string;
}

function StudentsSection() {
  const { t } = useTranslation();
  const { data: family, isLoading } = useMyFamily();
  const createStudent = useCreateFamilyStudent();
  const updateStudent = useUpdateFamilyStudent();
  const removeStudent = useRemoveFamilyStudent();
  const uploadPhoto = useUploadStudentPhoto();

  // null = closed; 'new' = create modal; otherwise the student being edited
  const [editing, setEditing] = useState<'new' | FamilyStudent | null>(null);
  const [form, setForm] = useState<StudentFormState>({
    firstName: '',
    lastName: '',
    birthDate: '',
    email: '',
  });
  const [pendingPhoto, setPendingPhoto] = useState<Blob | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const students = (family?.students ?? []).filter((s) => s.isActive);

  const openCreate = () => {
    setForm({ firstName: '', lastName: '', birthDate: '', email: '' });
    setPendingPhoto(null);
    setError(null);
    setEditing('new');
  };

  const openEdit = (student: FamilyStudent) => {
    setForm({
      firstName: student.firstName,
      lastName: student.lastName,
      birthDate: student.birthDate.slice(0, 10),
      email: student.email ?? '',
    });
    setPendingPhoto(null);
    setError(null);
    setEditing(student);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editing === 'new') {
        // Email is optional on creation
        const created = await createStudent.mutateAsync({
          ...form,
          email: form.email || undefined,
        });
        if (pendingPhoto) {
          await uploadPhoto.mutateAsync({ id: created.userId, blob: pendingPhoto });
        }
      } else if (editing) {
        // On update, empty string removes the email
        await updateStudent.mutateAsync({ id: editing.userId, payload: form });
        if (pendingPhoto) {
          await uploadPhoto.mutateAsync({ id: editing.userId, blob: pendingPhoto });
        }
      }
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('guardian.students.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (student: FamilyStudent) => {
    if (!(await confirmDialog({ message: t('guardian.students.confirmRemove', { name: student.name }), tone: 'danger' }))) return;
    await removeStudent.mutateAsync(student.userId);
  };

  return (
    <section>
      <PageHead action={<Button type="button" onClick={openCreate}>
          {t('guardian.students.add')}
        </Button>} sub={t('guardian.students.hint')}>{t('guardian.students.title')}</PageHead>

      {isLoading && <p className={styles.guardianEmpty}>{t('common.loading')}</p>}

      {!isLoading && students.length === 0 && (
        <p className={styles.guardianEmpty}>{t('guardian.students.empty')}</p>
      )}

      <div className={styles.guardianStudentGrid}>
        {students.map((student) => (
          <Card key={student.userId} framed padded={false} className={styles.guardianStudentCard}>
            <img
              src={student.avatarPhotoUrl || defaultAvatar}
              alt=""
              className={`${styles.guardianStudentCard__avatar}`}
            />
            <div className={`${styles.guardianStudentCard__info}`}>
              <span className={`${styles.guardianStudentCard__name}`}>{student.name}</span>
              <span className={`${styles.guardianStudentCard__birth}`}>
                {t('guardian.students.birthDate')}: {student.birthDate.slice(0, 10)}
              </span>
              {student.email && (
                <span className={`${styles.guardianStudentCard__birth}`}>{student.email}</span>
              )}
            </div>
            <div className={`${styles.guardianStudentCard__actions}`}>
              <Button type="button" variant="gold" size="sm" onClick={() => openEdit(student)}
              >
                {t('common.edit')}
              </Button>
              <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleRemove(student)}
              >
                {t('guardian.students.remove')}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {editing !== null && (
        <Modal
          isOpen
          onClose={() => !saving && setEditing(null)}
          size="mdWide"
          disableBackdropClose
          title={editing === 'new' ? t('guardian.students.add') : t('guardian.students.edit')}
        >
          <form onSubmit={(e) => void handleSave(e)}>
            <ProfilePhotoField
              buttonVariant="gold"
              currentUrl={editing !== 'new' ? editing.avatarPhotoUrl : undefined}
              onPendingChange={setPendingPhoto}
            />
            <div className={styles.guardianFormRow}>
              <FormField label={t('signup.firstName')} required>
                {(field) => (
                  <Input {...field}
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    required
                    disabled={saving}
                  />
                )}
              </FormField>
              <FormField label={t('signup.lastName')} required>
                {(field) => (
                  <Input {...field}
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    required
                    disabled={saving}
                  />
                )}
              </FormField>
            </div>
            <FormField label={t('signup.birthDate')} required>
              {(field) => (
                <Input {...field}
                  type="date"
                  value={form.birthDate}
                  onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
                  required
                  disabled={saving}
                />
              )}
            </FormField>
            <FormField
              label={t('guardian.students.emailLabel')}
              hint={t('guardian.students.emailHint')}
            >
              {(field) => (
                <Input
                  {...field}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={saving}
                />
              )}
            </FormField>
            {error && <FormMessage>{error}</FormMessage>}
            <div className={styles.guardianFormActions}>
              <Button type="button" variant="ghost" onClick={() => setEditing(null)}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saving || !form.firstName || !form.lastName || !form.birthDate}>
                {saving ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Guardians (co-guardians of the family)                              */
/* ------------------------------------------------------------------ */

function GuardiansSection() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { data: family, isLoading } = useMyFamily();
  const inviteGuardian = useInviteGuardian();
  const removeGuardian = useRemoveGuardian();

  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', email: '' });
  const [error, setError] = useState<string | null>(null);
  const [inviteSent, setInviteSent] = useState(false);

  const guardians = (family?.guardians ?? []).filter((g) => g.isActive);
  const canRemove = (g: FamilyGuardian) => g.userId !== user?.id && guardians.length > 1;

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInviteSent(false);
    try {
      await inviteGuardian.mutateAsync({ ...inviteForm, locale: i18n.language });
      setInviteSent(true);
      setShowInvite(false);
      setInviteForm({ firstName: '', lastName: '', email: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('guardian.guardians.inviteError'));
    }
  };

  const handleRemove = async (guardian: FamilyGuardian) => {
    if (!(await confirmDialog({ message: t('guardian.guardians.confirmRemove', { name: guardian.name }), tone: 'danger' }))) return;
    setError(null);
    try {
      await removeGuardian.mutateAsync(guardian.userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('guardian.guardians.removeError'));
    }
  };

  return (
    <section>
      <PageHead action={<Button type="button" onClick={() => setShowInvite(true)}
        >
          {t('guardian.guardians.invite')}
        </Button>} sub={t('guardian.guardians.hint')}>{t('guardian.guardians.title')}</PageHead>

      {inviteSent && <FormMessage tone="success">{t('guardian.guardians.inviteSent')}</FormMessage>}
      {error && <FormMessage>{error}</FormMessage>}
      {isLoading && <p className={styles.guardianEmpty}>{t('common.loading')}</p>}

      <div className={styles.guardianStudentGrid}>
        {guardians.map((guardian) => (
          <Card key={guardian.userId} framed padded={false} className={styles.guardianStudentCard}>
            <img
              src={guardian.avatarPhotoUrl || defaultAvatar}
              alt=""
              className={`${styles.guardianStudentCard__avatar}`}
            />
            <div className={`${styles.guardianStudentCard__info}`}>
              <span className={`${styles.guardianStudentCard__name}`}>
                {guardian.name}
                {guardian.userId === user?.id && ` (${t('guardian.guardians.you')})`}
              </span>
              <span className={`${styles.guardianStudentCard__birth}`}>{guardian.email}</span>
              {!guardian.emailVerified && (
                <Pill tone="warning">{t('guardian.guardians.pendingInvite')}</Pill>
              )}
            </div>
            {canRemove(guardian) && (
              <div className={`${styles.guardianStudentCard__actions}`}>
                <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleRemove(guardian)}
                >
                  {t('guardian.students.remove')}
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {showInvite && (
        <Modal
          isOpen
          onClose={() => setShowInvite(false)}
          size="mdWide"
          disableBackdropClose
          title={t('guardian.guardians.inviteTitle')}
        >
          <p className={styles.guardianDialogHint}>{t('guardian.guardians.inviteHint')}</p>
          <form onSubmit={(e) => void handleInvite(e)}>
            <div className={styles.guardianFormRow}>
              <FormField label={t('signup.firstName')} required>
                {(field) => (
                  <Input {...field}
                    type="text"
                    value={inviteForm.firstName}
                    onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                    required
                  />
                )}
              </FormField>
              <FormField label={t('signup.lastName')} required>
                {(field) => (
                  <Input {...field}
                    type="text"
                    value={inviteForm.lastName}
                    onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                    required
                  />
                )}
              </FormField>
            </div>
            <FormField label={t('login.email')} required>
              {(field) => (
                <Input {...field}
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  required
                />
              )}
            </FormField>
            {error && <FormMessage>{error}</FormMessage>}
            <div className={styles.guardianFormActions}>
              <Button type="button" variant="ghost" onClick={() => setShowInvite(false)}
                disabled={inviteGuardian.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={ inviteGuardian.isPending || !inviteForm.firstName || !inviteForm.lastName || !inviteForm.email }>
                {inviteGuardian.isPending ? t('common.loading') : t('guardian.guardians.sendInvite')}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Devices                                                             */
/* ------------------------------------------------------------------ */

function DevicesSection() {
  const { t } = useTranslation();
  const { data: devices, isLoading } = useFamilyDevices();
  const linkDevice = useLinkFamilyDevice();
  const revokeDevice = useRevokeFamilyDevice();

  const [deviceName, setDeviceName] = useState('');
  const [linked, setLinked] = useState(hasFamilyDeviceToken());
  const [error, setError] = useState<string | null>(null);

  const activeDevices = (devices ?? []).filter((d) => !d.revoked);

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await linkDevice.mutateAsync(deviceName);
      // Persist the raw token — it is only ever returned this once
      setFamilyDeviceToken(result.deviceToken);
      setLinked(true);
      setDeviceName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('guardian.devices.linkError'));
    }
  };

  const handleRevoke = async (id: string, name: string) => {
    if (!(await confirmDialog({ message: t('guardian.devices.confirmRevoke', { name }), tone: 'danger' }))) return;
    await revokeDevice.mutateAsync(id);
  };

  return (
    <section>
      <PageHead sub={t('guardian.devices.hint')}>{t('guardian.devices.title')}</PageHead>

      <div className={styles.guardianDeviceLinkBox}>
        {linked ? (
          <p className={styles.guardianDeviceLinked}>✓ {t('guardian.devices.thisDeviceLinked')}</p>
        ) : (
          <form onSubmit={(e) => void handleLink(e)} className={styles.guardianDeviceLinkForm}>
            <Input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder={t('guardian.devices.namePlaceholder')}
              disabled={linkDevice.isPending}
              required
            />
            <Button type="submit" disabled={!deviceName || linkDevice.isPending}>
              {linkDevice.isPending ? t('common.loading') : t('guardian.devices.linkThis')}
            </Button>
          </form>
        )}
        {error && <FormMessage>{error}</FormMessage>}
      </div>

      {isLoading && <p className={styles.guardianEmpty}>{t('common.loading')}</p>}
      {!isLoading && activeDevices.length === 0 && (
        <p className={styles.guardianEmpty}>{t('guardian.devices.empty')}</p>
      )}

      <ul className={styles.guardianDeviceList}>
        {activeDevices.map((device) => (
          <li key={device.id} className={styles.guardianDeviceItem}>
            <div className={`${styles.guardianDeviceItem__info}`}>
              <span className={`${styles.guardianDeviceItem__name}`}>{device.name}</span>
              <span className={`${styles.guardianDeviceItem__meta}`}>
                {t('guardian.devices.lastUsed')}: {device.lastUsedAt.slice(0, 10)}
              </span>
            </div>
            <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleRevoke(device.id, device.name)}
            >
              {t('guardian.devices.revoke')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Account                                                             */
/* ------------------------------------------------------------------ */

function AccountSection() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { data: family, isLoading } = useMyFamily();
  const updateProfile = useUpdateGuardianProfile();
  const uploadPhoto = useUploadGuardianPhoto();

  const me = family?.guardians.find((g) => g.userId === user?.id);

  // Guardian data is minimal by design: name, phone, photo and access PIN
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const [initialized, setInitialized] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form once from the fetched profile
  if (me && !initialized) {
    setForm({
      firstName: me.firstName ?? '',
      lastName: me.lastName ?? '',
      phoneNumber: me.phoneNumber ?? '',
    });
    setInitialized(true);
  }

  /** Cropped photo from ProfilePhotoField (deferred mode) — upload right away */
  const handlePhotoChange = (blob: Blob | null) => {
    if (!blob) return;
    setError(null);
    uploadPhoto.mutate(blob, {
      onError: (err) =>
        setError(err instanceof Error ? err.message : t('guardian.account.saveError')),
    });
  };

  const setField = (field: keyof typeof form, value: string) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await updateProfile.mutateAsync(form);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('guardian.account.saveError'));
    }
  };

  if (isLoading) {
    return <p className={styles.guardianEmpty}>{t('common.loading')}</p>;
  }

  return (
    <section>
      <PageHead
        sub={
          <>
            {t('guardian.account.emailLabel')}: <strong>{me?.email}</strong>
          </>
        }
      >
        {t('guardian.account.title')}
      </PageHead>

      <form onSubmit={(e) => void handleSubmit(e)} className={`${styles.guardianAccountForm} framed`}>
        <FormBlock label={t('guardian.account.photo')}>
          <ProfilePhotoField buttonVariant="gold" currentUrl={me?.avatarPhotoUrl} onPendingChange={handlePhotoChange} /></FormBlock>

        <div className={styles.guardianFormRow}>
          <FormField label={t('signup.firstName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t('signup.lastName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                required
              />
            )}
          </FormField>
        </div>

        <FormField label={t('signup.phone')}>
          {(field) => (
            <Input {...field}
              type="tel"
              value={form.phoneNumber}
              onChange={(e) => setField('phoneNumber', e.target.value)}
            />
          )}
        </FormField>

        {error && <FormMessage>{error}</FormMessage>}
        {saved && <FormMessage tone="success">{t('guardian.account.saved')}</FormMessage>}

        <div className={styles.guardianFormActions}>
          <Button type="submit" disabled={updateProfile.isPending}>
            {updateProfile.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
